/**
 * Cursor pagination — the request schema, the response envelope, and the one place a cursor is
 * encoded or decoded (`docs/design/00-contracts.md` §12.5; C-27, C-28).
 *
 * Principle 6: cursor-based pagination on every list, never `OFFSET`, never `COUNT(*)`. The
 * cursor is an opaque base64url JSON object:
 *
 *   { "v": 2, "s": "due_at:asc", "k": ["2027-06-12T10:00:00Z", "018f4e1c-...-4b21"] }
 *
 * `v` is the format version — bump it when the shape changes and older cursors are rejected with
 * 400 `INVALID_CURSOR`. `s` is the sort spec the cursor was minted for, so reusing a cursor after
 * changing the sort returns 400 rather than silently producing wrong pages. `k` is the keyset
 * tuple: exactly the `ORDER BY` columns, in order, ending in `id`.
 *
 * `k` HOLDS AS MANY VALUES AS THE SORT HAS COLUMNS, WHICH IS NOT ALWAYS TWO. A sort whose
 * leading column repeats — company, in a register where two thousand people work for forty
 * companies — needs a second **visible** column before the id, or the reader is handed a page
 * ordered by a UUID they cannot see. A keyset has to compare exactly what the `ORDER BY` orders,
 * so the cursor grew with it:
 *
 *   { "v": 2, "s": "company:asc", "k": ["Live Nation", "Nora Nilsen", "018f4e1c-...-4b21"] }
 *
 * `k` IS EXACTLY THE `ORDER BY` TUPLE, WHICH IS SOMETIMES JUST THE ID. A sort on a column that
 * is already unique — `created`, where the UUIDv7 id **is** the creation order — orders by one
 * column and its cursor carries one value. The id is always last and always a string, which is
 * what keeps a cursor a position rather than a row count. Version 1 cursors carried a fixed pair
 * and are refused.
 *
 * A cursor is opaque but **not secret**: it encodes a position, and RLS still decides what is
 * visible from it, so a forged cursor cannot reach another organization's rows. It is validated
 * strictly anyway.
 *
 * Base64 is implemented here by hand rather than with `Buffer` or `btoa`: this module runs in the
 * browser bundle as well as in Node, and neither global is available in both.
 */
import { z } from 'zod';

/** C-28: 01 and 04 agree on the default, 03 and 04 on the maximum. */
export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 100;
/** A cursor longer than this is not one of ours; reject before parsing. */
export const MAX_CURSOR_LENGTH = 512;
/** Bump when the cursor payload shape changes. Version 2 made `k` variable length. */
export const CURSOR_VERSION = 2;

/**
 * The widest keyset any list may page on, the id included.
 *
 * A cap rather than a preference: `k` arrives off the wire and every value in it is bound into a
 * row comparison, so an unbounded array is an unbounded query. Four is one wider than the widest
 * sort in the API — company, which orders by (company, display name, id) — and small enough that
 * a cursor stays well inside `MAX_CURSOR_LENGTH`.
 */
export const MAX_KEYSET_COLUMNS = 4;

export type SortDirection = 'asc' | 'desc';
export const sortDirectionSchema = z.enum(['asc', 'desc']).meta({ id: 'SortDirection' });

/** `"<sort>:<direction>"` — the spec a cursor is minted for and validated against. */
export function sortSpec(sort: string, direction: SortDirection): string {
  return `${sort}:${direction}`;
}

/**
 * The query parameters of every list endpoint, parameterised by the sort keys that endpoint
 * supports. The first key is the default sort.
 */
export const cursorQuerySchema = <const T extends readonly [string, ...string[]]>(sortKeys: T) =>
  z.object({
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    cursor: z.string().max(MAX_CURSOR_LENGTH).optional(),
    sort: z.enum(sortKeys).default(sortKeys[0]),
    direction: sortDirectionSchema.default('asc'),
  });

export type CursorQuery<TSortKey extends string = string> = {
  limit: number;
  cursor?: string | undefined;
  sort: TSortKey;
  direction: SortDirection;
};

/** Every list response in the API has exactly this shape. No exceptions, no ad-hoc envelopes. */
export function page<TItem extends z.ZodType>(item: TItem, id: string) {
  return z
    .object({
      items: z.array(item),
      nextCursor: z.string().nullable(),
      hasMore: z.boolean(),
    })
    .meta({ id });
}

export type Page<T> = { items: T[]; nextCursor: string | null; hasMore: boolean };

/** One value of the keyset tuple. `id`, the last of them, is always a string. */
export type CursorKeyValue = string | number | boolean;

export const cursorPayloadSchema = z.object({
  v: z.literal(CURSOR_VERSION),
  /** The sort spec the cursor was produced under: "name:asc". */
  s: z.string().min(1).max(128),
  /**
   * The keyset tuple: every `ORDER BY` value in order, ending in the row id.
   *
   * At least one entry — a sort on an already-unique column orders by the id alone — and the last
   * of them is a string, because the tiebreaker is a UUID. Both rules are enforced here rather
   * than trusted downstream: `k` is caller-supplied text that ends up inside a row comparison.
   * How MANY entries a given sort expects is `keysetOf`'s question, not this schema's.
   */
  k: z
    .array(z.union([z.string(), z.number(), z.boolean()]))
    .min(1)
    .max(MAX_KEYSET_COLUMNS)
    .refine((keys) => typeof keys[keys.length - 1] === 'string', {
      message: 'The last keyset value is the row id and must be a string.',
    }),
});

export type CursorPayload = z.infer<typeof cursorPayloadSchema>;

/** Thrown by `decodeCursor()`. `apps/api` maps it to 400 `INVALID_CURSOR`. */
export class InvalidCursorError extends Error {
  readonly code = 'INVALID_CURSOR' as const;

  constructor(message: string) {
    super(message);
    this.name = 'InvalidCursorError';
  }
}

const B64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

const B64_LOOKUP: ReadonlyMap<string, number> = new Map(
  [...B64_ALPHABET].map((char, index) => [char, index]),
);

/** Always called with 0..63; the fallback exists only to satisfy `noUncheckedIndexedAccess`. */
const b64Char = (index: number): string => B64_ALPHABET.charAt(index);

function bytesToBase64Url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i] ?? 0;
    const b1 = bytes[i + 1];
    const b2 = bytes[i + 2];
    out += b64Char(b0 >> 2);
    out += b64Char(((b0 & 0x03) << 4) | ((b1 ?? 0) >> 4));
    if (b1 === undefined) break;
    out += b64Char(((b1 & 0x0f) << 2) | ((b2 ?? 0) >> 6));
    if (b2 === undefined) break;
    out += b64Char(b2 & 0x3f);
  }
  return out;
}

function base64UrlToBytes(value: string): Uint8Array {
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const char of value) {
    const digit = B64_LOOKUP.get(char);
    if (digit === undefined) throw new InvalidCursorError('Cursor is not base64url.');
    buffer = (buffer << 6) | digit;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }
  return Uint8Array.from(bytes);
}

/**
 * Mints the cursor for the last row of a page.
 *
 * `sortValues` is the `ORDER BY` tuple **without** the tiebreaker; the id is appended here, so
 * one place knows that the id goes last and no caller can forget it.
 */
export function encodeCursor(
  spec: string,
  sortValues: readonly CursorKeyValue[],
  id: string,
): string {
  const payload: CursorPayload = { v: CURSOR_VERSION, s: spec, k: [...sortValues, id] };
  return bytesToBase64Url(new TextEncoder().encode(JSON.stringify(payload)));
}

/**
 * Validates a cursor strictly and refuses one minted for a different sort order, which would
 * otherwise silently skip or repeat rows.
 */
export function decodeCursor(cursor: string, expectedSortSpec: string): CursorPayload {
  if (cursor.length === 0 || cursor.length > MAX_CURSOR_LENGTH) {
    throw new InvalidCursorError('Cursor is missing or too long.');
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(base64UrlToBytes(cursor)));
  } catch (error) {
    if (error instanceof InvalidCursorError) throw error;
    throw new InvalidCursorError('Cursor is not decodable.');
  }

  const result = cursorPayloadSchema.safeParse(parsed);
  if (!result.success) throw new InvalidCursorError('Cursor payload is not valid.');
  if (result.data.s !== expectedSortSpec) {
    throw new InvalidCursorError(
      'Cursor was issued for a different sort order. Restart pagination.',
    );
  }
  return result.data;
}

/**
 * The cursor's keyset, checked against the number of columns the sort actually orders by.
 *
 * The one place that check happens, and it has to happen somewhere: `decodeCursor` knows the
 * sort SPEC a cursor was minted for but not how wide that sort's tuple is, so a cursor minted
 * before a sort grew a column passes the spec check and would otherwise be bound into a row
 * comparison of the wrong width — `(a, b, c) > (a, b)`, which is a database error where a 400
 * belongs. Callers pass the length of their own `ORDER BY`, and get back a keyset they may index
 * into up to that length.
 */
export function keysetOf(cursor: CursorPayload, columns: number): readonly CursorKeyValue[] {
  if (cursor.k.length !== columns) {
    throw new InvalidCursorError(
      'Cursor was issued for a different sort order. Restart pagination.',
    );
  }
  return cursor.k;
}

/**
 * Turns `limit + 1` fetched rows into the response envelope. Endpoints always fetch one row more
 * than they return: that extra row is the existence proof for `hasMore` and costs one index entry
 * instead of a second full scan for a count.
 *
 * `sortValuesOf` returns the sort columns of the last row **in `ORDER BY` order and without the
 * id**. Return fewer of them than the sort has columns and the next page's keyset is narrower
 * than its own `ORDER BY`, which is the bug that repeats a row at every page boundary.
 */
export function buildPage<TRow extends { id: string }>(
  rows: readonly TRow[],
  limit: number,
  spec: string,
  sortValuesOf: (row: TRow) => readonly CursorKeyValue[],
): Page<TRow> {
  const hasMore = rows.length > limit;
  const items = hasMore ? rows.slice(0, limit) : [...rows];
  const last = items.at(-1);
  return {
    items,
    nextCursor: hasMore && last ? encodeCursor(spec, sortValuesOf(last), last.id) : null,
    hasMore,
  };
}

/**
 * MARKØR PÅ EN LISTE SOM HAR VÆRT HEL (AI-ANALYSE §8.3, 26.10).
 *
 * Lister som `/attachments`, `/tasks/:id/comments` og en programposts reiser svarte med alle radene.
 * De får `limit` og `cursor` som alle andre — men standarden er listas gamle tak, så nettsiden, som
 * ikke sender `limit`, får det samme som før. En klient som vil bla, sender `limit` og følger
 * `nextCursor`. Sorteringen er listas egen (fast); bare markøren er ny.
 */
export const pageQuerySchema = (defaultLimit: number, maxLimit: number = defaultLimit) =>
  z.object({
    limit: z.coerce.number().int().min(1).max(maxLimit).default(defaultLimit),
    cursor: z.string().max(MAX_CURSOR_LENGTH).optional(),
  });

export interface PageQuery {
  readonly limit: number;
  readonly cursor?: string | undefined;
}

function compareKey(a: CursorKeyValue, b: CursorKeyValue): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const left = String(a);
  const right = String(b);
  return left < right ? -1 : left > right ? 1 : 0;
}

/**
 * Keyset over en liste som allerede er satt sammen og sortert i minnet — kalenderen, som slår sammen
 * sju kilder og bretter ut serier, kan ikke bla i SQL. `keyOf` gir sorteringstuppelen for en rad,
 * MED id-en sist; lista må være sortert på nøyaktig den tuppelen. Ingen OFFSET: markøren er den siste
 * radens nøkkel, så en rad som kommer til eller forsvinner flytter ikke de neste sidene.
 */
export function pageSorted<T>(
  sorted: readonly T[],
  query: PageQuery,
  spec: string,
  keyOf: (item: T) => readonly CursorKeyValue[],
): Page<T> {
  let start = 0;
  if (query.cursor !== undefined) {
    const first = sorted[0];
    const width = first === undefined ? 1 : keyOf(first).length;
    const after = keysetOf(decodeCursor(query.cursor, spec), width);
    start = sorted.findIndex((item) => {
      const key = keyOf(item);
      for (let i = 0; i < key.length; i += 1) {
        const order = compareKey(key[i] as CursorKeyValue, after[i] as CursorKeyValue);
        if (order !== 0) return order > 0;
      }
      return false;
    });
    if (start < 0) start = sorted.length;
  }
  const items = sorted.slice(start, start + query.limit);
  const hasMore = start + query.limit < sorted.length;
  const last = items.at(-1);
  if (!hasMore || last === undefined) return { items, nextCursor: null, hasMore: false };
  const key = keyOf(last);
  return {
    items,
    nextCursor: encodeCursor(spec, key.slice(0, -1), String(key[key.length - 1])),
    hasMore: true,
  };
}

/**
 * Markørfeltene på en liste som var hel (§8.3). Valgfrie i TYPEN, så en klient som bygde `{ items }`
 * selv (optimistiske oppdateringer, testdata) fortsatt typer; serveren sender alltid begge.
 */
export const pageCursorFields = {
  nextCursor: z.string().nullable().optional(),
  hasMore: z.boolean().optional(),
};

/** Standardspørringen for de hele listene uten egne filtre: 500 rader, som er listenes gamle tak. */
export const listPageQuerySchema = pageQuerySchema(500).meta({ id: 'ListPageQuery' });
export type ListPageQuery = z.infer<typeof listPageQuerySchema>;

/**
 * Som `pageSorted`, men for en liste i en orden JavaScript ikke kan sammenligne (sortert med norsk
 * kollasjon i basen, eller av en funksjon): markøren peker på RADEN, ikke på en verdi. Er raden borte
 * siden forrige side, må klienten begynne på nytt (`INVALID_CURSOR`), aldri hoppe over noe stille.
 */
export function pageAnchored<T>(
  list: readonly T[],
  query: PageQuery,
  spec: string,
  idOf: (item: T) => string,
): Page<T> {
  let start = 0;
  if (query.cursor !== undefined) {
    const [anchor] = keysetOf(decodeCursor(query.cursor, spec), 1);
    const index = list.findIndex((item) => idOf(item) === anchor);
    if (index < 0)
      throw new InvalidCursorError('The row the cursor points at is gone. Restart pagination.');
    start = index + 1;
  }
  const items = list.slice(start, start + query.limit);
  const hasMore = start + query.limit < list.length;
  const last = items.at(-1);
  return {
    items,
    nextCursor: hasMore && last !== undefined ? encodeCursor(spec, [], idOf(last)) : null,
    hasMore,
  };
}
